import React, { useCallback, useMemo, useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, UploadCloudIcon, DownloadIcon, TrashIcon, AlertCircleIcon, CheckCircleIcon, FileSpreadsheetIcon, XIcon } from 'lucide-react';
// Template columns matching the Supplier CRUD fields
const TEMPLATE_COLUMNS = [{
  key: 'supplierType',
  label: 'Supplier Type',
  hint: 'company or individual',
  required: true
}, {
  key: 'name',
  label: 'Name',
  hint: 'Company or individual name',
  required: true
}, {
  key: 'email',
  label: 'Email',
  hint: 'Contact email',
  required: true
}, {
  key: 'phone',
  label: 'Phone',
  hint: 'Phone number',
  required: false
}, {
  key: 'taxId',
  label: 'Tax ID',
  hint: '13-digit tax ID',
  required: false
}, {
  key: 'companyNameTh',
  label: 'Company Name (TH)',
  hint: 'Thai name',
  required: false
}, {
  key: 'companyNameEn',
  label: 'Company Name (EN)',
  hint: 'English name',
  required: false
}, {
  key: 'branch',
  label: 'Branch',
  hint: 'e.g. 00000',
  required: false
}, {
  key: 'vatRegistered',
  label: 'VAT Registered',
  hint: 'true or false',
  required: false
}, {
  key: 'contactPerson',
  label: 'Contact Person',
  hint: 'Contact name',
  required: false
}, {
  key: 'addressLine1',
  label: 'Address Line 1',
  hint: 'Street address',
  required: false
}, {
  key: 'addressLine2',
  label: 'Address Line 2',
  hint: 'District',
  required: false
}, {
  key: 'city',
  label: 'City',
  hint: 'City name',
  required: false
}, {
  key: 'province',
  label: 'Province',
  hint: 'Province',
  required: false
}, {
  key: 'postcode',
  label: 'Postcode',
  hint: 'Postal code',
  required: false
}, {
  key: 'internalReference',
  label: 'Internal Reference',
  hint: 'Your internal ref',
  required: false
}, {
  key: 'payoutMethod',
  label: 'Payout Method',
  hint: 'bank_account or promptpay',
  required: false
}, {
  key: 'bankName',
  label: 'Bank Name',
  hint: 'Bank name',
  required: false
}, {
  key: 'accountNumber',
  label: 'Account Number',
  hint: 'Bank account number',
  required: false
}, {
  key: 'accountName',
  label: 'Account Name',
  hint: 'Account holder name',
  required: false
}];
interface ParsedRow {
  id: string;
  data: Record<string, string>;
  errors: string[];
}
function generateCSVTemplate(): string {
  const header = TEMPLATE_COLUMNS.map(c => c.label).join(',');
  const sampleRow = ['company', 'Acme Corp Ltd', 'contact@acmecorp.com', '021234567', '0105563012345', 'บริษัท เอซมี จำกัด', 'Acme Corp Ltd', '00000', 'true', 'John Doe', '123 Main Street', 'Khlong Toei', 'Bangkok', 'Bangkok', '10110', 'SUP-NEW', 'bank_account', 'Kasikorn Bank (KBANK)', '0381236789', 'Acme Corp Ltd'].join(',');
  return `${header}\n${sampleRow}`;
}
function parseCSV(text: string): {
  headers: string[];
  rows: string[][];
} {
  const lines = text.split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return {
    headers: [],
    rows: []
  };
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = lines.slice(1).map(line => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;
    for (const char of line) {
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  });
  return {
    headers,
    rows
  };
}
function mapHeaderToKey(header: string): string | null {
  const normalized = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const col of TEMPLATE_COLUMNS) {
    const colNorm = col.label.toLowerCase().replace(/[^a-z0-9]/g, '');
    if (normalized === colNorm) return col.key;
    const keyNorm = col.key.toLowerCase();
    if (normalized === keyNorm) return col.key;
  }
  return null;
}
function validateRow(data: Record<string, string>): string[] {
  const errors: string[] = [];
  if (!data.name) errors.push('Name is required');
  if (!data.email) errors.push('Email is required');
  if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('Invalid email format');
  }
  if (!data.supplierType) {
    errors.push('Supplier type is required');
  } else if (!['company', 'individual'].includes(data.supplierType.toLowerCase())) {
    errors.push('Supplier type must be "company" or "individual"');
  }
  if (data.payoutMethod && !['bank_account', 'promptpay'].includes(data.payoutMethod.toLowerCase())) {
    errors.push('Payout method must be "bank_account" or "promptpay"');
  }
  return errors;
}
export function BulkSupplierUpload() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState('');
  const validRows = useMemo(() => parsedRows.filter(r => r.errors.length === 0), [parsedRows]);
  const errorRows = useMemo(() => parsedRows.filter(r => r.errors.length > 0), [parsedRows]);
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'supplier_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };
  const processFile = useCallback((file: File) => {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      const {
        headers,
        rows
      } = parseCSV(text);
      if (headers.length === 0 || rows.length === 0) {
        setParseError('The file appears to be empty or has no data rows.');
        return;
      }
      // Map headers to keys
      const keyMap: (string | null)[] = headers.map(mapHeaderToKey);
      const unmapped = headers.filter((_, i) => keyMap[i] === null);
      if (unmapped.length > 0 && keyMap.filter(Boolean).length === 0) {
        setParseError(`Could not match any columns. Please use the template format. Unrecognized headers: ${unmapped.join(', ')}`);
        return;
      }
      const parsed: ParsedRow[] = rows.map((values, idx) => {
        const data: Record<string, string> = {};
        keyMap.forEach((key, i) => {
          if (key && values[i] !== undefined) {
            data[key] = values[i];
          }
        });
        return {
          id: `row-${idx}`,
          data,
          errors: validateRow(data)
        };
      });
      setParsedRows(parsed);
      setStep('preview');
    };
    reader.readAsText(file);
  }, []);
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  }, [processFile]);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => setIsDragging(false);
  const removeRow = (id: string) => {
    setParsedRows(prev => prev.filter(r => r.id !== id));
  };
  const handleImport = () => {
    // In a real app, this would POST to an API
    setStep('success');
  };
  const handleReset = () => {
    setStep('upload');
    setParsedRows([]);
    setFileName('');
    setParseError('');
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-6092">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-6093">
        <div className="flex items-center space-x-4" data-id="element-6094">
          <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-6095">
            <ArrowLeftIcon size={16} className="mr-2" data-id="element-6096" />
            Back to Suppliers
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6097">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-6098">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-6099">
            Bulk Upload Suppliers
          </h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-6100">
            Upload a CSV file to add multiple suppliers at once.
          </p>
        </div>

        <div className="p-8" data-id="element-6101">
          {/* ========== UPLOAD STEP ========== */}
          {step === 'upload' && <div className="space-y-8" data-id="element-6102">
              {/* Template download */}
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-5 flex items-center justify-between" data-id="element-6103">
                <div className="flex items-center" data-id="element-6104">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center mr-4" data-id="element-6105">
                    <FileSpreadsheetIcon size={20} className="text-green-700" data-id="element-6106" />
                  </div>
                  <div data-id="element-6107">
                    <p className="text-sm font-medium text-gray-900" data-id="element-6108">
                      Download template
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-6109">
                      Use this CSV template to format your supplier data
                      correctly. All required fields are marked.
                    </p>
                  </div>
                </div>
                <button onClick={handleDownloadTemplate} className="flex items-center px-4 py-2 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-6110">
                  <DownloadIcon size={16} className="mr-2" data-id="element-6111" />
                  Download CSV
                </button>
              </div>

              {/* Dropzone */}
              <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all ${isDragging ? 'border-[#F7941D] bg-[#F7941D]/5' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'}`} data-id="element-6112">
                <input type="file" accept=".csv,.txt" onChange={handleFileInput} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-6113" />
                <div className="flex flex-col items-center" data-id="element-6114">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 ${isDragging ? 'bg-[#F7941D]/10' : 'bg-gray-100'}`} data-id="element-6115">
                    <UploadCloudIcon size={28} className={isDragging ? 'text-[#F7941D]' : 'text-gray-400'} data-id="element-6116" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-6117">
                    {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file here'}
                  </p>
                  <p className="text-xs text-gray-500 mb-4" data-id="element-6118">
                    or click to browse
                  </p>
                  <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md" data-id="element-6119">
                    Supported: .csv
                  </span>
                </div>
              </div>

              {parseError && <div className="flex items-start p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-6120">
                  <AlertCircleIcon size={18} className="text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-6121" />
                  <div data-id="element-6122">
                    <p className="text-sm font-medium text-red-800" data-id="element-6123">
                      Upload error
                    </p>
                    <p className="text-xs text-red-600 mt-0.5" data-id="element-6124">{parseError}</p>
                  </div>
                </div>}

              {/* Field reference */}
              <div data-id="element-6125">
                <h3 className="text-sm font-medium text-gray-900 mb-3" data-id="element-6126">
                  Expected fields
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-id="element-6127">
                  {TEMPLATE_COLUMNS.map(col => <div key={col.key} className="flex items-center text-xs text-gray-600 py-1.5 px-2.5 bg-gray-50 rounded-md border border-gray-100" data-id="element-6128">
                      <span className="font-medium text-gray-800 mr-1" data-id="element-6129">
                        {col.label}
                      </span>
                      {col.required && <span className="text-red-400 text-[10px]" data-id="element-6130">*</span>}
                    </div>)}
                </div>
              </div>
            </div>}

          {/* ========== PREVIEW STEP ========== */}
          {step === 'preview' && <div className="space-y-6" data-id="element-6131">
              {/* Summary bar */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg border border-gray-200 px-5 py-4" data-id="element-6132">
                <div className="flex items-center space-x-6" data-id="element-6133">
                  <div className="flex items-center text-sm" data-id="element-6134">
                    <FileSpreadsheetIcon size={16} className="text-gray-400 mr-2" data-id="element-6135" />
                    <span className="font-medium text-gray-900" data-id="element-6136">
                      {fileName}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-sm" data-id="element-6137">
                    <span className="text-gray-600" data-id="element-6138">
                      <span className="font-semibold text-gray-900" data-id="element-6139">
                        {parsedRows.length}
                      </span>{' '}
                      rows total
                    </span>
                    <span className="text-green-600" data-id="element-6140">
                      <span className="font-semibold" data-id="element-6141">{validRows.length}</span>{' '}
                      valid
                    </span>
                    {errorRows.length > 0 && <span className="text-red-600" data-id="element-6142">
                        <span className="font-semibold" data-id="element-6143">
                          {errorRows.length}
                        </span>{' '}
                        with errors
                      </span>}
                  </div>
                </div>
                <button onClick={handleReset} className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-6144">
                  Upload different file
                </button>
              </div>

              {/* Error rows */}
              {errorRows.length > 0 && <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-id="element-6145">
                  <div className="flex items-center mb-3" data-id="element-6146">
                    <AlertCircleIcon size={16} className="text-red-500 mr-2" data-id="element-6147" />
                    <span className="text-sm font-medium text-red-800" data-id="element-6148">
                      {errorRows.length} row{errorRows.length > 1 ? 's' : ''}{' '}
                      with validation errors
                    </span>
                  </div>
                  <p className="text-xs text-red-600 mb-3" data-id="element-6149">
                    These rows have issues and will not be imported. Fix them in
                    your file or remove them below.
                  </p>
                  <div className="space-y-2" data-id="element-6150">
                    {errorRows.map(row => <div key={row.id} className="flex items-center justify-between bg-white rounded-md border border-red-200 px-4 py-2.5" data-id="element-6151">
                        <div className="flex-1 min-w-0" data-id="element-6152">
                          <span className="text-sm font-medium text-gray-900 mr-3" data-id="element-6153">
                            {row.data.name || '(no name)'}
                          </span>
                          <span className="text-xs text-gray-500 mr-3" data-id="element-6154">
                            {row.data.email || '(no email)'}
                          </span>
                          <span className="text-xs text-red-600" data-id="element-6155">
                            {row.errors.join('; ')}
                          </span>
                        </div>
                        <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors ml-3" data-id="element-6156">
                          <TrashIcon size={14} data-id="element-6157" />
                        </button>
                      </div>)}
                  </div>
                </div>}

              {/* Preview table */}
              {validRows.length > 0 && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-6158">
                  <div className="overflow-x-auto" data-id="element-6159">
                    <table className="w-full text-left" data-id="element-6160">
                      <thead data-id="element-6161">
                        <tr className="bg-gray-50 border-b border-gray-200" data-id="element-6162">
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-10" data-id="element-6163">
                            #
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6164">
                            Type
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6165">
                            Name
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6166">
                            Email
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6167">
                            Phone
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6168">
                            Tax ID
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6169">
                            Province
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-6170">
                            Payout
                          </th>
                          <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-12" data-id="element-6171"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100" data-id="element-6172">
                        {validRows.map((row, idx) => <tr key={row.id} className="hover:bg-gray-50 transition-colors" data-id="element-6173">
                            <td className="px-4 py-3 text-xs text-gray-400" data-id="element-6174">
                              {idx + 1}
                            </td>
                            <td className="px-4 py-3" data-id="element-6175">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${row.data.supplierType?.toLowerCase() === 'company' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`} data-id="element-6176">
                                {row.data.supplierType || '-'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-6177">
                              {row.data.name || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-6178">
                              {row.data.email || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-6179">
                              {row.data.phone || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs" data-id="element-6180">
                              {row.data.taxId || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-6181">
                              {row.data.province || '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600" data-id="element-6182">
                              {row.data.payoutMethod || '-'}
                            </td>
                            <td className="px-4 py-3" data-id="element-6183">
                              <button onClick={() => removeRow(row.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-md hover:bg-gray-100 transition-colors" data-id="element-6184">
                                <TrashIcon size={14} data-id="element-6185" />
                              </button>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}

              {parsedRows.length === 0 && <div className="text-center py-12 text-gray-500 text-sm" data-id="element-6186">
                  All rows have been removed. Upload a different file to try
                  again.
                </div>}

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-gray-100" data-id="element-6187">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-6188">
                  Cancel
                </button>
                <button onClick={handleImport} disabled={validRows.length === 0} className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm ${validRows.length > 0 ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-6189">
                  Import {validRows.length} supplier
                  {validRows.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>}

          {/* ========== SUCCESS STEP ========== */}
          {step === 'success' && <div className="text-center py-16" data-id="element-6190">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6" data-id="element-6191">
                <CheckCircleIcon size={32} className="text-green-600" data-id="element-6192" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-6193">
                Suppliers imported successfully
              </h2>
              <p className="text-sm text-gray-500 mb-8" data-id="element-6194">
                {validRows.length} supplier
                {validRows.length !== 1 ? 's have' : ' has'} been added to your
                account.
              </p>
              <div className="flex justify-center gap-3" data-id="element-6195">
                <button onClick={handleReset} className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-6196">
                  Upload more
                </button>
                <button onClick={() => navigate('/suppliers')} className="px-5 py-2.5 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-6197">
                  Go to Suppliers
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}